<template>
    <v-list-item class="px-0 d-flex entries text-body-1" :class="{'text-warning': isProductSoldOut}">
        <template v-slot:prepend>
            <v-list-item-action class="entries__prepend-block d-lg-flex align-center flex-shrink-0">
                <v-checkbox
                    v-if="showPriceDuringSearch && !entry.obligatory && !entry.base"
                    :model-value="selected"
                    color="primary"
                    :disabled="isProductSoldOut && !isTO1"
                    hide-details
                    class="ms-2"
                    @click="selectEntry(entry)"
                />
                <div v-else class="mr-7" />
            </v-list-item-action>
        </template>
        <div class="d-lg-flex align-center gc-3">
            <div v-if="!hideDate" class="entries__time">
                {{ $filters.dateFormat(date) }}
            </div>
            <div v-if="!hideEntryType" class="entries__type">
                <v-icon size="small">
                    {{ getIcon(entry) }}
                </v-icon>
                {{ getEntryType(entry) }}
            </div>
            <div v-if="selectable" class="entries__duration">
                <hotel-service-duration-select
                    v-if="entry.type === 'ACCOMMODATION' && entry.duration"
                    :entry="entry"
                    :offer="offer"
                    :citizenship-id="citizenshipId"
                    :search-request="searchRequest"
                />
            </div>
            <div v-if="entry.type === 'ACCOMMODATION'" class="flex-grow-1">
                <div class="d-lg-flex align-center flex-wrap">
                    <p class="text-h6 font-weight-regular">{{ selectedHotel(entry).name }}</p>
                    <hotel-stars class="mb-1" :hotel="selectedHotel(entry)" />
                    <div class="d-none d-md-flex d-lg-block align-center gc-2">
                        <v-btn
                            v-if="offer.info.staticPrice !== true"
                            color="primary"
                            variant="text"
                            icon="mdi-information-outline"
                            size="small"
                            density="comfortable"
                            @click="showServiceInfo(entry)"
                        />
                        <span v-if="entry.offers.length > 1" class="text-link" @click="showServiceInfo(entry)">
                            {{ $t('tours.alternative_hotels') }}
                        </span>
                    </div>
                    <v-chip v-if="isHotelSoldOut" size="small">
                        {{ $t('unavailable') }}
                    </v-chip>
                </div>
                <div class="text-medium-emphasis">
                    {{ `${selectedHotel(entry).cityName}, ${selectedHotel(entry).countryName}` }}
                </div>
                <div class="text-medium-emphasis">
                    {{ `${selectedRoom(entry).name}, ${selectedRoom(entry).mealTypeName}` }}
                </div>
                <div class="d-flex align-center d-md-none">
                    <v-btn
                        color="primary"
                        variant="text"
                        class="d-lg-none"
                        icon="mdi-information-outline"
                        size="small"
                        density="comfortable"
                        @click="showServiceInfo(entry)"
                    />
                    <span v-if="entry.offers.length > 1" class="text-link" @click="showServiceInfo(entry)">
                        {{ $t('tours.alternative_hotels') }}
                    </span>
                </div>
            </div>
            <div v-else-if="entry.type === 'CAR'" class="flex-grow-1">
                <div class="d-flex align-center">
                    <p class="text-h6 font-weight-regular">
                        {{ getProductName(entry) }}
                        <v-btn
                            color="primary"
                            variant="text"
                            icon="mdi-information-outline"
                            size="small"
                            density="comfortable"
                            @click="showServiceInfo(entry)"
                        />
                    </p>
                    <v-chip v-if="isProductSoldOut" size="small" color="warning">
                        {{ $t('unavailable') }}
                    </v-chip>
                </div>
                <car-feature-labels class="py-2" :info="entry.info" small />
                <car-characteristic :info="entry.info" x-small />
            </div>
            <div v-else class="flex-grow-1">
                <div class="d-flex align-center">
                    <p class="text-h6 font-weight-regular">
                        {{ getProductName(entry) }}
                        <v-btn
                            color="primary"
                            variant="text"
                            icon="mdi-information-outline"
                            size="small"
                            density="comfortable"
                            @click="showServiceInfo(entry)"
                        />
                    </p>
                    <span
                        v-if="entry.type === 'FLIGHT' && entry.offers.length > 1"
                        class="text-link"
                        @click="showServiceInfo(entry)"
                    >
                        {{ $t('flights.alternative_flights') }}
                    </span>
                    <v-chip v-if="isProductSoldOut" size="small" color="warning">
                        {{ $t('unavailable') }}
                    </v-chip>
                </div>
                <div v-if="getProductServiceName(entry)" class="text-medium-emphasis">
                    {{ `${getProductServiceName(entry)}` }}
                </div>
            </div>
            <strong
                v-if="showPriceDuringSearch && !entry.obligatory && !entry.base && price"
                class="mr-3 flex-shrink-0"
            >
                {{ $filters.price(price) }}
            </strong>
        </div>
    </v-list-item>
</template>

<script lang="ts">
    import {Component, Prop, Setup, Vue} from 'vue-facing-decorator'
    import {addDays, format} from 'date-fns'
    import {
        EventBus,
        SHOW_TOUR_ACTIVITY_INFO,
        SHOW_TOUR_CAR_INFO,
        SHOW_TOUR_CRUISE_INFO,
        SHOW_TOUR_EXTRA_SERVICE_INFO,
        SHOW_TOUR_FLIGHT_INFO,
        SHOW_TOUR_HOTEL_INFO,
        SHOW_TOUR_INSURANCE_INFO,
        SHOW_TOUR_TRANSFER_INFO,
    } from '@/utils/event-bus'
    import HotelStars from '~src/components/hotels/snippets/hotelStars.src'
    import HotelServiceDurationSelect from '@/components/account/modals/newOrderRequest/forms/HotelServiceDurationSelect.vue'
    import CarFeatureLabels from '@/components/carsRent/snippets/CarFeatureLabels.vue'
    import CarCharacteristic from '@/components/carsRent/snippets/CarCharacteristic.vue'
    import {tourEntrySelectedOffer} from '~/utils/tours/tour-helpers'

    @Component({
        components: {HotelServiceDurationSelect, HotelStars, CarFeatureLabels, CarCharacteristic},
    })
    export class TourOfferEntry extends Vue {
        @Prop({required: true}) entry
        @Prop({required: true}) offer
        @Prop({required: false, type: Boolean}) hideDate
        @Prop({required: false, type: Boolean}) hideEntryType
        @Prop({required: false, type: Boolean}) selectable
        @Prop() searchRequest
        @Prop() citizenshipId
        @Setup(() => useBookingPriceButton().showPriceDuringSearch) showPriceDuringSearch

        selectEntry(entry) {
            //TODO Need refactoring
            const selectedOfferKey =
                entry.type === 'ACCOMMODATION'
                    ? this.selectedRoom(this.entry).groupedOffers[0].offerKey
                    : entry.type === 'CAR'
                      ? entry.offer.offerKey
                      : entry.offers[0].offers
                        ? entry.offers[0].offers[0].offerKey
                        : entry.offers[0].offerKey
            if (!this.selected) {
                toursRuntimeStore.SELECT_OFFER({offerKey: this.offerKey, selectedOfferKey})
            } else {
                toursRuntimeStore.UNSELECT_OFFER({offerKey: this.offerKey, selectedOfferKey})
            }
        }

        showServiceInfo(entry) {
            switch (entry.type) {
                case 'ACCOMMODATION':
                    EventBus.$emit(SHOW_TOUR_HOTEL_INFO, {entry, offerKey: this.offerKey})
                    return
                case 'FLIGHT':
                    EventBus.$emit(SHOW_TOUR_FLIGHT_INFO, {entry, offerKey: this.offerKey})
                    return
                case 'TRANSFER':
                    EventBus.$emit(SHOW_TOUR_TRANSFER_INFO, {entry, offerKey: this.offerKey})
                    return
                case 'EVENT':
                case 'EXCURSION':
                    EventBus.$emit(SHOW_TOUR_ACTIVITY_INFO, {entry, offerKey: this.offerKey})
                    return
                case 'EXTRA_SERVICE':
                    EventBus.$emit(SHOW_TOUR_EXTRA_SERVICE_INFO, {entry, offerKey: this.offerKey})
                    return
                case 'INSURANCE':
                    EventBus.$emit(SHOW_TOUR_INSURANCE_INFO, {entry, offerKey: this.offerKey})
                    return
                case 'CRUISE':
                    EventBus.$emit(SHOW_TOUR_CRUISE_INFO, {entry, offerKey: this.offerKey})
                    return
                case 'CAR':
                    EventBus.$emit(SHOW_TOUR_CAR_INFO, {entry, offerKey: this.offerKey})
                    return
            }
        }

        getIcon({type}) {
            switch (type) {
                case 'ACCOMMODATION':
                    return 'mdi-office-building'
                case 'TRANSFER':
                    return 'mdi-bus'
                case 'EXCURSION':
                case 'EVENT':
                    return 'mdi-lightning-bolt-outline'
                case 'FLIGHT':
                    return 'mdi-airplane'
                case 'INSURANCE':
                    return 'mdi-umbrella'
                case 'EXTRA_SERVICE':
                    return 'mdi-star-box'
                case 'CRUISE':
                    return 'mdi-ferry'
                case 'CAR':
                    return 'mdi-car'
                default:
                    return 'mdi-help'
            }
        }

        getEntryType(item) {
            switch (item.type) {
                case 'ACCOMMODATION':
                    return 'HOTEL'
                case 'EXTRA_SERVICE':
                    return 'EXTRA SERVICE'
                default:
                    return item.type
            }
        }

        getProductName(item) {
            switch (item.type) {
                case 'ACCOMMODATION':
                case 'TRANSFER':
                case 'EXCURSION':
                case 'EVENT':
                case 'INSURANCE':
                case 'EXTRA_SERVICE':
                case 'CRUISE':
                case 'CAR':
                    return item.name
                case 'FLIGHT':
                    return item.flightName || item.name
                default:
                    return item.productName
            }
        }

        getProductServiceName(entry) {
            switch (entry.type) {
                case 'ACCOMMODATION':
                    return ''
                case 'FLIGHT':
                    return entry.offers?.[0].itinerary[0].segments[0].class.classType || ''
                case 'EVENT':
                case 'EXCURSION':
                    for (const product of entry.offers) {
                        const selectedOffer = product.offers.find(({offerKey}) => offerKey === entry.selectedOfferKey)
                        if (selectedOffer) return selectedOffer.serviceName
                    }
                    return ''
                case 'EXTRA_SERVICE':
                    return entry.selectedOfferKey
                        ? entry.offers.find(offer => offer.offerKey === entry.selectedOfferKey).info.service
                        : entry.offers[0].info.service
                case 'INSURANCE':
                    return entry.selectedOfferKey
                        ? entry.offers.find(offer => offer.offerKey === entry.selectedOfferKey).planName
                        : entry.offers[0].planName
                case 'TRANSFER':
                    return entry.selectedOfferKey
                        ? entry.offers.find(offer => offer.offerKey === entry.selectedOfferKey).info.description
                        : entry.offers[0].info.description
                case 'CRUISE':
                    for (const product of entry.offers) {
                        const selectedOffer = product.offers.find(({offerKey}) => offerKey === entry.selectedOfferKey)
                        if (selectedOffer)
                            return `${selectedOffer.serviceName} / ${selectedOffer.categoryName} / ${
                                selectedOffer.mealType?.name || ''
                            }`
                    }
                    return ''
                case 'CAR':
                    return entry.name
                default:
                    return ''
            }
        }

        selectedHotel(entry) {
            if (!entry.obligatory && !entry.selectedOfferKey) return entry.offers[0]
            return hotelsRuntimeStore.findHotelByOfferKey(entry.offers, entry.selectedOfferKey)
        }

        selectedRoom(entry) {
            if (!entry.obligatory && !entry.selectedOfferKey) return entry.offers[0].rooms[0]

            return this.selectedHotel(entry).rooms.find(
                ({groupedOffers}) => groupedOffers[0].offerKey === entry.selectedOfferKey
            )
        }

        get date() {
            if (this.entry.entryDate) {
                return this.entry.entryDate
            } else if (this.entry.dayNumber > 1) {
                return format(addDays(new Date(this.offer.info.packageDate), this.entry.dayNumber - 1), 'yyyy-MM-dd')
            } else {
                return this.offer.info.packageDate
            }
        }

        get offerKey() {
            return this.offer.offerKey
        }

        get selected() {
            return !!this.entry.selectedOfferKey
        }

        get isHotelSoldOut() {
            const isHotel = this.entry.type === 'ACCOMMODATION'

            if (!isHotel) {
                return false
            }

            let selectedRoom

            this.entry?.offers.forEach(offer => {
                const room = offer.rooms.find(room => {
                    return !!room.groupedOffers.find(g => g.offerKey === this.entry?.selectedOfferKey)
                })

                if (room) {
                    selectedRoom = room
                    return undefined
                }
            })

            return !!selectedRoom?.soldOut
        }

        get isProductSoldOut() {
            return !!tourEntrySelectedOffer(this.entry, true)?.soldOut
        }

        get isTO1() {
            return authStore.isTO1
        }

        get price() {
            return tourEntrySelectedOffer(this.entry, true)?.notDeltaPrice
        }
    }

    export default TourOfferEntry
</script>

<style lang="scss" scoped>
    .entries {
        :deep(.v-list-item__prepend) {
            flex-shrink: 0 !important;
        }
        :deep(.v-list-item__content) {
            width: 100%;
        }
        &__time,
        &__duration,
        &__type {
            flex: 0 0 150px;
        }
    }
</style>
